// =============================================================================
// features/documents/components/DocumentStatusBadge.tsx — Badge de status do
// documento (F46-S08).
//
// Cruza `scan_status` (worker de segurança, F46-S06) com `status` (ciclo de
// vida de revisão) num único badge — o usuário nunca precisa entender os dois
// campos separadamente. Prioridade: scan primeiro (documento "rejeitado pelo
// scan" nunca deve parecer "aguardando revisão").
// =============================================================================

import * as React from 'react';

import { Badge, type BadgeVariant } from '../../../components/ui/Badge';

export interface DocumentStatusInput {
  status: 'pending_review' | 'approved' | 'rejected' | 'expired';
  scan_status: 'pending' | 'clean' | 'rejected';
}

export interface DocumentStatusPresentation {
  label: string;
  variant: BadgeVariant;
}

/**
 * Resolve o par (scan_status, status) para um único badge.
 * Função pura — testada isoladamente em __tests__/DocumentStatusBadge.test.ts.
 */
export function resolveDocumentStatusBadge(doc: DocumentStatusInput): DocumentStatusPresentation {
  if (doc.scan_status === 'pending') {
    return { label: 'Verificando…', variant: 'warning' };
  }
  if (doc.scan_status === 'rejected') {
    return { label: 'Arquivo recusado', variant: 'danger' };
  }

  switch (doc.status) {
    case 'approved':
      return { label: 'Aprovado', variant: 'success' };
    case 'rejected':
      return { label: 'Rejeitado', variant: 'danger' };
    case 'expired':
      return { label: 'Vencido', variant: 'warning' };
    case 'pending_review':
    default:
      return { label: 'Aguardando revisão', variant: 'info' };
  }
}

export interface DocumentStatusBadgeProps {
  document: DocumentStatusInput;
}

export function DocumentStatusBadge({ document }: DocumentStatusBadgeProps): React.JSX.Element {
  const presentation = resolveDocumentStatusBadge(document);
  return <Badge variant={presentation.variant}>{presentation.label}</Badge>;
}
